import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import Checkbox from '@/components/ui/Checkbox';
import SymbolSourceRefFields from '@/components/admin/symbols/SymbolSourceRefFields';
import { useSymbolSourceRefs } from '@/hooks/useSymbolSourceRefs';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showErrorToast } from '@/utils/toasts';
import { normalizeSourceRef } from '@/utils/symbol-source';
import type { Symbol, StockExchange, StockExchangeID } from '@/models';
import type { UpdateSymbolRequest } from '@/modelsAPI';
import {PAGINATION} from "@/config";

interface EditSymbolModalProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: Symbol;
  onSymbolUpdated: (symbol: Symbol) => void;
}

export default function EditSymbolModal({ isOpen, onClose, symbol, onSymbolUpdated }: EditSymbolModalProps) {
  const [ticker, setTicker] = useState('');
  const [name, setName] = useState('');
  const [sector, setSector] = useState('');
  const [showInTicker, setShowInTicker] = useState(false);
  const [exchangeID, setExchangeID] = useState<StockExchangeID | ''>('');
  const [stockExchanges, setStockExchanges] = useState<StockExchange[]>([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const sourceRefs = useSymbolSourceRefs({ ticker, exchangeID, symbolId: symbol.id, autoDetect: false });

  useEffect(() => {
    if (isOpen) {
      fetchStockExchanges();
    }
  }, [isOpen]);

  useEffect(() => {
    if (symbol !== null) {
      setTicker(symbol.ticker);
      setName(symbol.name ?? '');
      setSector(symbol.sector ?? '');
      setShowInTicker(symbol.showInTicker);
      setExchangeID(symbol.stockExchangeId);
      sourceRefs.reset(symbol.googleSheets.ref ?? '', symbol.eodhd.ref ?? '');
    }
  }, [symbol]);

  const fetchStockExchanges = async () => {
    try {
      setLoadingExchanges(true);
      const response = await APIRoutes.listStockExchanges({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setStockExchanges(response.stockExchanges);
    } catch (err) {
      console.error('Error loading stock exchanges:', err);
      handleAPIError(err);
    } finally {
      setLoadingExchanges(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ticker.trim()) {
      showErrorToast('Bitte geben Sie ein Ticker-Symbol ein');
      return;
    }

    if (exchangeID === '') {
      showErrorToast('Bitte wählen Sie eine Börse aus');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    if (!sourceRefs.refsValid) {
      showErrorToast('Bitte das Format der Datenquellen-Referenzen prüfen');
      return;
    }

    setLoading(true);

    try {
      const request: UpdateSymbolRequest = {};
      if (newTicker !== symbol.ticker) {
        request.ticker = newTicker;
      }
      if (name.trim() !== (symbol.name ?? '')) {
        request.name = name.trim() !== '' ? name.trim() : null;
      }
      if (sector.trim() !== (symbol.sector ?? '')) {
        request.sector = sector.trim() !== '' ? sector.trim() : null;
      }
      if (showInTicker !== symbol.showInTicker) {
        request.showInTicker = showInTicker;
      }
      if (exchangeID !== symbol.stockExchangeId) {
        request.stockExchangeId = exchangeID;
      }
      if (googleSheetsRefChanged) {
        request.googleSheetsRef = newGoogleSheetsRef !== '' ? newGoogleSheetsRef : null;
      }
      if (eodhdRefChanged) {
        request.eodhdRef = newEodhdRef !== '' ? newEodhdRef : null;
      }

      const updatedSymbol = await APIRoutes.updateSymbol(symbol.id, request);
      onSymbolUpdated(updatedSymbol);
      onClose();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren des Symbols');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTicker(symbol.ticker);
      setName(symbol.name ?? '');
      setSector(symbol.sector ?? '');
      setShowInTicker(symbol.showInTicker);
      setExchangeID(symbol.stockExchangeId);
      sourceRefs.reset(symbol.googleSheets.ref ?? '', symbol.eodhd.ref ?? '');
      onClose();
    }
  };

  const newTicker = ticker.trim().toUpperCase();
  const newGoogleSheetsRef = normalizeSourceRef(sourceRefs.googleSheetsRef);
  const newEodhdRef = normalizeSourceRef(sourceRefs.eodhdRef);
  const googleSheetsRefChanged = newGoogleSheetsRef !== (symbol.googleSheets.ref ?? '');
  const eodhdRefChanged = newEodhdRef !== (symbol.eodhd.ref ?? '');
  const identityChanged = newTicker !== symbol.ticker || exchangeID !== symbol.stockExchangeId;

  const hasChanges = identityChanged ||
                     name.trim() !== (symbol.name ?? '') ||
                     sector.trim() !== (symbol.sector ?? '') ||
                     showInTicker !== symbol.showInTicker ||
                     googleSheetsRefChanged ||
                     eodhdRefChanged;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Symbol bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="lg"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="mb-6">
          <label htmlFor="ticker" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ticker-Symbol
          </label>
          <input
            type="text"
            id="ticker"
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. AAPL, MSFT, TSLA"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Geben Sie das Börsen-Ticker-Symbol ein (z.B. AAPL für Apple Inc.)
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. Apple Inc., Microsoft Corporation"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Geben Sie den vollständigen Firmennamen ein (optional)
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="sector" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Branche
          </label>
          <input
            type="text"
            id="sector"
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. Technologie, Finanzen, Gesundheitswesen"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Geben Sie die Branche des Unternehmens ein (optional)
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="exchange" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Börse
          </label>
          <select
            id="exchange"
            value={exchangeID}
            onChange={(e) => setExchangeID(e.target.value as StockExchangeID)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white cursor-pointer"
            disabled={loading || loadingExchanges}
          >
            <option value="">Börse auswählen...</option>
            {stockExchanges.map((exchange) => (
              <option key={exchange.id} value={exchange.id}>
                {exchange.identifier} - {exchange.name}
              </option>
            ))}
          </select>
          {loadingExchanges && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin mr-1" />
              Lade Börsen...
            </p>
          )}
        </div>

        <SymbolSourceRefFields
          refs={sourceRefs}
          canVerify={ticker.trim() !== '' && exchangeID !== ''}
          disabled={loading}
          accent="blue"
          current={{ googleSheets: symbol.googleSheets, eodhd: symbol.eodhd }}
          notice={identityChanged && !googleSheetsRefChanged && !eodhdRefChanged
            ? 'Ticker oder Börse wurden geändert – die Referenzen werden dabei nicht angepasst. Ggf. „Automatisch ermitteln“ verwenden.'
            : undefined}
        />

        <div className="mb-6">
          <Checkbox
            checked={showInTicker}
            onChange={setShowInTicker}
            disabled={loading}
            label="Im Ticker anzeigen"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
            Wenn aktiviert, wird dieses Symbol im unteren Ticker angezeigt
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !ticker.trim() || exchangeID === '' || !hasChanges || !sourceRefs.refsValid}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichere...
              </>
            ) : (
              'Änderungen speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
